import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CircleCheckIcon from '../../../assets/svg_components/CircleCheckIcon';

import Button from '../../../components/Button';
import SuspenseLoader from '../../../components/Loader/SuspenseLoader';
import { PROCESS_DRAFT_GUARANTEE } from '../../../graphql/mutation';
import ResponseModal from '../../../modal_views/global/ResponseModal';

import formatCash from '../../../util/helpers/formatCash';
import useToast from '../../../util/hooks/useToast';

import OneTimePaymentStyles from './OneTimePayment.module.scss';

interface IOneTimePayment {
  payload: any;
  address: string;
  type: string;
  loading: boolean;
  id: any;
}
const OneTimePayment = ({ payload, address, type, loading, id }: IOneTimePayment) => {
  const navigate = useNavigate();
  const Toast = useToast();
  const [makeProcessDraftGuarantee, { loading: processDraftGuaranteeLoading }] = useMutation<
    { processDraftGuarantee: any },
    any
  >(PROCESS_DRAFT_GUARANTEE);

  const [isSuccessful, setIsSuccessful] = useState(false);
  const paymentBreakdown = [
    {
      id: 1,
      title: 'Rent Guarantee Fee',
      value: formatCash(payload?.guaranteeFee, 'C$', false, true)
    },
    {
      id: 2,
      title: 'Processing Fee',
      value: formatCash(payload?.processingFee, 'C$', false, true)
    },
    {
      id: 3,
      title: 'First and last month rent value',
      value: formatCash(payload?.firstAndlastMonthPayment, 'C$', false, true)
    },
    {
      ...(type === 'installment'
        ? {
            id: 4,
            title: 'Credit Tenor',
            value: `${payload?.tenor} months`
          }
        : '')
    },

    {
      id: 5,
      title: 'Total',
      value: formatCash(payload?.totalAmountPayable, 'C$', false, true),
      hasDivider: true
    },
    {
      ...(type === 'installment'
        ? {
            id: 6,
            title: 'Amount to pay today',
            value: formatCash(payload?.dueToday, 'C$', false, true)
          }
        : '')
    },
    {
      ...(type === 'installment'
        ? {
            id: 7,
            title: 'Monthly installment amount',
            value: formatCash(payload?.monthlyInstallmentAmount, 'C$', false, true)
          }
        : '')
    }
  ];

  const handleSubmit = async () => {
    const variables: any = {
      rentGuaranteeId: id,
      isInstallment: type === 'installment'
    };
    if (!processDraftGuaranteeLoading) {
      try {
        const res = await makeProcessDraftGuarantee({ variables });

        if (res?.data?.processDraftGuarantee && res?.data?.processDraftGuarantee?.status === 200) {
          setIsSuccessful(true);

          // Toast.success(res?.data?.processDraftGuarantee?.message || 'Operation successful');
        } else {
          Toast.error(res?.data?.processDraftGuarantee?.message || 'Operation Failed');
        }
      } catch (e: any) {
        if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
          Toast.error(e.graphQLErrors[0].message);
        } else {
          Toast.error('Something went wrong');
        }
      }
    }
  };

  return loading ? (
    <div className={OneTimePaymentStyles.wrapper}>
      <div className={OneTimePaymentStyles.wrapper__loader}>
        <SuspenseLoader />
      </div>
    </div>
  ) : (
    <div className={OneTimePaymentStyles.wrapper}>
      <div className={OneTimePaymentStyles.wrapper__breakdownTitleAndValue}>
        <div className={OneTimePaymentStyles.wrapper__title}>Total payment</div>
        <div className={OneTimePaymentStyles.wrapper__value}>
          {formatCash(payload?.totalAmountPayable, 'C$', false, true)}
        </div>
      </div>
      {type === 'installment' ? (
        <div className={OneTimePaymentStyles.wrapper__note}>
          Please note that you are to pay{' '}
          <strong>{formatCash(payload?.totalAmountPayable, 'C$', false, true)}</strong> now and{' '}
          <strong>{formatCash(payload?.monthlyInstallmentAmount, 'C$', false, true)}</strong>{' '}
          monthly for your rent guarantee application at{' '}
          <strong className={OneTimePaymentStyles.wrapper__address}>{address}</strong>
        </div>
      ) : (
        <div className={OneTimePaymentStyles.wrapper__note}>
          Please note that you are to pay{' '}
          <strong>{formatCash(payload?.totalAmountPayable, 'C$', false, true)}</strong> at{' '}
          <strong className={OneTimePaymentStyles.wrapper__address}>{address}</strong>
        </div>
      )}

      <div className={OneTimePaymentStyles.wrapper__breakdownTag}>Here is the breakdown:</div>
      {paymentBreakdown?.map((list) =>
        list?.title ? (
          <div
            key={list?.id}
            className={`${
              list?.hasDivider
                ? OneTimePaymentStyles.wrapper__breakdownTitleAndValueWithDivider
                : OneTimePaymentStyles.wrapper__breakdownTitleAndValue
            }`}>
            <div className={OneTimePaymentStyles.wrapper__breakdownTitle}>{list?.title}</div>
            <div className={OneTimePaymentStyles.wrapper__breakdownValue}>{list?.value}</div>
          </div>
        ) : (
          ''
        )
      )}
      <div className={OneTimePaymentStyles.wrapper__buttons}>
        <div className={OneTimePaymentStyles.wrapper__pay}>
          <Button
            title="Submit for Approval"
            onPress={handleSubmit}
            isLoading={processDraftGuaranteeLoading}
          />
        </div>
        <div className={OneTimePaymentStyles.wrapper__back}>
          <Button
            title="Back"
            onPress={() => navigate('/dashboard/request-guarantee-records?page=1')}
          />
        </div>
      </div>
      {isSuccessful && (
        <ResponseModal
          isShow
          onClickAway={() => {}}
          onBack={() => {}}
          onClose={() => {}}
          title="Pending Approval"
          isShowCloseIcon={false}
          icon={<CircleCheckIcon fillColor="#5941A9" width="68px" height="68px" />}
          subTitle="Your rent guarantee application has been submitted and will be reviewed by our team. An email will be sent on the status of your application.."
          btnTitle="Go to Rent Guarantee"
          onPress={() => navigate('/dashboard/request-guarantee-records?page=1')}
        />
      )}
    </div>
  );
};

export default OneTimePayment;
